import React from "react";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { faGem } from "@fortawesome/pro-regular-svg-icons";
import { faWreathLaurel } from "@fortawesome/pro-duotone-svg-icons";
import { LeaderboardsResponse } from "../../common/api";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { Account, AccountContext } from "../../common/context/AccountContext";
import { ApiContext } from "../../common/context/ApiContext";
import { useLiveData } from "../../common/utils";
import { SingleLineText } from "../../common/CommonStyles";
import { DuotoneIcon, STAT_ICONS } from "../../components/profile/ProfileIcons";
import {
  COLOR_ELEVATION_SHADOW,
  COLOR_GRAY_CC,
  COLOR_NEUTRAL_BORDER,
  COLOR_SURFACE_CONTAINER,
  COLOR_SURFACE_CONTAINER_STRONG,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_TERTIARY,
  COLOR_WHITE,
} from "../../common/colors";
import {
  SPACE_LG,
  SPACE_MD,
  SPACE_SM,
  SPACE_XL,
  SPACE_XS,
} from "../../common/spacing";
import { GENERIC_ITEM_BORDER_RADIUS } from "../../common/constants";

const MEDAL_COLORS: Record<number, { primary: string; secondary: string }> = {
  1: {
    primary: STAT_ICONS.courses.primaryColor,
    secondary: STAT_ICONS.courses.secondaryColor,
  },
  2: { primary: "#C9D3DC", secondary: "#9AA7B4" },
  3: { primary: "#E8A25E", secondary: "#B86F2E" },
};

const LeaderboardCard = styled.div`
  max-width: 640px;
  margin: ${SPACE_XL}px auto;
  padding: ${SPACE_XL}px;
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
  background-color: ${COLOR_WHITE};
  border: 1px solid ${COLOR_NEUTRAL_BORDER};
  box-shadow: 0 1px 2px 1px ${COLOR_ELEVATION_SHADOW};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE_SM}px;
  margin-bottom: ${SPACE_LG}px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: ${COLOR_TEXT_PRIMARY};
`;

const MonthButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${SPACE_SM}px;
  padding: ${SPACE_SM}px ${SPACE_LG}px;
  border: none;
  border-radius: 1000px;
  background-color: ${COLOR_SURFACE_CONTAINER_STRONG};
  font-size: 14px;
  font-weight: 500;
  color: ${COLOR_TEXT_PRIMARY};
`;

const Row = styled.div<{ $isCurrentProfile: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACE_MD}px;
  height: 48px;
  margin: 0 -${SPACE_SM}px;
  padding: 0 ${SPACE_SM}px;
  border-radius: 8px;
  background-color: ${(props) =>
    props.$isCurrentProfile ? COLOR_SURFACE_CONTAINER : "transparent"};
`;

const RankCell = styled.div`
  flex: 0 0 34px;
  display: flex;
  justify-content: center;
`;

const MedalBox = styled.div`
  position: relative;
  width: 34px;
  height: 27px;
`;

const MedalNumber = styled.span`
  position: absolute;
  left: 50%;
  top: 45%;
  transform: translate(-50%, -50%);
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: ${COLOR_WHITE};
  font-size: 10px;
  font-weight: 600;
  line-height: 15px;
  text-align: center;
  color: ${COLOR_TEXT_SECONDARY};
`;

const RankNumber = styled.span`
  min-width: 24px;
  height: 24px;
  padding: 0 ${SPACE_XS}px;
  border-radius: 1000px;
  background-color: ${COLOR_SURFACE_CONTAINER_STRONG};
  font-size: 13px;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
  color: ${COLOR_TEXT_SECONDARY};
`;

const StudentName = styled(SingleLineText)`
  flex: 1;
  font-size: 18px;
  color: ${COLOR_TEXT_PRIMARY};
  text-decoration: underline;
  text-decoration-color: ${COLOR_GRAY_CC};
  text-underline-offset: 4px;
`;

const GemCount = styled.span`
  display: flex;
  align-items: center;
  gap: ${SPACE_XS}px;
  font-size: 18px;
  color: ${COLOR_TEXT_SECONDARY};

  svg {
    font-size: 14px;
    color: ${COLOR_TEXT_TERTIARY};
  }
`;

const EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  padding: ${SPACE_XL}px;
  text-align: center;
  font-size: 16px;
  color: ${COLOR_TEXT_SECONDARY};
`;

const monthName = (monthsAgo: number, language: string) => {
  const today = new Date();
  return new Date(
    today.getFullYear(),
    today.getMonth() - monthsAgo,
    1
  ).toLocaleString(language, { month: "long" });
};

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  const colors = MEDAL_COLORS[rank];
  if (!colors) {
    return <RankNumber>{rank}</RankNumber>;
  }
  return (
    <MedalBox>
      <DuotoneIcon
        icon={faWreathLaurel}
        {...colors}
        style={{ width: "100%", height: "100%" }}
      />
      <MedalNumber>{rank}</MedalNumber>
    </MedalBox>
  );
};

export const LeaderboardsTab: React.FC = () => {
  const [state, setState] = React.useState<LeaderboardsResponse>({
    this_month_leaderboard: [],
    last_month_leaderboard: [],
  });
  const [account, setAccount] = React.useState<Account>();
  const [monthsAgo, setMonthsAgo] = React.useState(0);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const { t, i18n } = useTranslation();
  const { accountLiveData } = React.useContext(AccountContext);
  const { getLeaderboards } = React.useContext(ApiContext);

  const refetchLeaderboards = React.useCallback(async () => {
    startLoading();
    setState(await getLeaderboards());
    stopLoading();
  }, [getLeaderboards, startLoading, stopLoading]);

  useLiveData(
    React.useCallback(
      (account: Account) => {
        setAccount(account);
        refetchLeaderboards();
      },
      [refetchLeaderboards]
    ),
    accountLiveData
  );

  const entries = [state.this_month_leaderboard, state.last_month_leaderboard][
    monthsAgo
  ];
  const isEmpty = entries.length === 0 || entries[0].gems_earned === 0;

  return (
    <LeaderboardCard>
      <CardHeader>
        <CardTitle>{t("profile_leaderboard")}</CardTitle>
        <MonthButton
          ref={anchorRef}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-haspopup="menu"
        >
          {monthName(monthsAgo, i18n.language)}
          <FontAwesomeIcon icon={faChevronDown} />
        </MonthButton>
        <Menu
          open={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          anchorEl={anchorRef.current}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          disableAutoFocusItem
          slotProps={{ paper: { style: { minWidth: 180, marginTop: 6 } } }}
        >
          {[0, 1].map((option) => (
            <MenuItem
              key={option}
              onClick={() => {
                setMonthsAgo(option);
                setIsMenuOpen(false);
              }}
            >
              <ListItemText>{monthName(option, i18n.language)}</ListItemText>
              {option === monthsAgo && (
                <ListItemIcon>
                  <FontAwesomeIcon icon={faCheck} />
                </ListItemIcon>
              )}
            </MenuItem>
          ))}
        </Menu>
      </CardHeader>
      {isEmpty ? (
        <EmptyMessage>
          <div>
            {monthsAgo === 0 ? (
              <Trans t={t} i18nKey="awards_leaderboard_tab_this_month_empty">
                Complete <strong>math and reading lessons</strong> to earn{" "}
                <strong>gems</strong>, and make it to the top of your school
                leaderboard each month!
              </Trans>
            ) : (
              <Trans t={t} i18nKey="awards_leaderboard_tab_last_month_empty">
                Each month, the <strong>top 10 students</strong> with the most
                gems will be featured here!
              </Trans>
            )}
          </div>
        </EmptyMessage>
      ) : (
        entries.map((it) => (
          <Row
            key={it.profile_id}
            $isCurrentProfile={it.profile_id === account?.profile?.id}
          >
            <RankCell>
              <RankBadge rank={it.rank} />
            </RankCell>
            <StudentName>{it.student_name}</StudentName>
            <GemCount>
              {it.gems_earned}
              <FontAwesomeIcon icon={faGem} />
            </GemCount>
          </Row>
        ))
      )}
    </LeaderboardCard>
  );
};
